import type { Track } from '../interfaces/track';
import type { Album } from '../interfaces/albums';
import type { Playlist } from '../interfaces/playlists';
import type { Artist } from '../interfaces/artist';

export function extractImage(images: any): { url: string; width: number; height: number }[] {
  if (!images) return [];

  const getHighQuality = (url: string) => (url ? url.replace(/150x150|50x50/g, '500x500') : url);

  if (typeof images === 'string') return [{ url: getHighQuality(images), width: 500, height: 500 }];
  if (!Array.isArray(images)) {
    if (images.link || images.url) return [{ url: getHighQuality(images.link || images.url), width: 500, height: 500 }];
    return [];
  }
  return images.map((img: any) => {
    let width = 500;
    if (img.quality) {
        const parts = img.quality.split('x');
        if (parts.length === 2) width = parseInt(parts[0], 10);
    }
    const rawUrl = typeof img === 'string' ? img : (img.link || img.url);
    return {
        url: getHighQuality(rawUrl),
        width,
        height: width
    };
  }).sort((a: any, b: any) => b.width - a.width);
}

function decodeStr(str: string) {
  if (!str) return '';
  return str.replace(/&quot;/g, '"').replace(/&amp;/g, '&');
}

function getArtists(item: any) {
  if (item.artists && Array.isArray(item.artists.primary) && item.artists.primary.length > 0) {
    return item.artists.primary.map((a: any) => ({
      external_urls: { spotify: '' },
      href: '',
      id: a.id || '0',
      name: decodeStr(a.name || a.title || 'Unknown Artist'),
      type: 'artist',
      uri: `spotify:artist:${a.id || '0'}`,
      image: extractImage(a.image)
    }));
  }
  if (item.more_info?.artistMap?.primary_artists && item.more_info.artistMap.primary_artists.length > 0) {
    return item.more_info.artistMap.primary_artists.map((a: any) => ({
      external_urls: { spotify: '' },
      href: '',
      id: a.id || '0',
      name: decodeStr(a.name || 'Unknown Artist'),
      type: 'artist',
      uri: `spotify:artist:${a.id || '0'}`,
      image: extractImage(a.image)
    }));
  }
  if (item.more_info?.artistMap?.artists && item.more_info.artistMap.artists.length > 0) {
    return item.more_info.artistMap.artists.map((a: any) => ({
      external_urls: { spotify: '' },
      href: '',
      id: a.id || '0',
      name: decodeStr(a.name || 'Unknown Artist'),
      type: 'artist',
      uri: `spotify:artist:${a.id || '0'}`,
      image: extractImage(a.image)
    }));
  }
  if (item.primaryArtists) {
    return [{
      external_urls: { spotify: '' },
      href: '',
      id: item.primaryArtistsId || '0',
      name: decodeStr(item.primaryArtists || item.subtitle || 'Unknown Artist'),
      type: 'artist',
      uri: `spotify:artist:${item.primaryArtistsId || '0'}`,
      image: extractImage(item.image)
    }];
  }
  const fallbackName = item.label || item.subtitle || (item.type === 'episode' ? 'Podcaster' : 'Unknown Artist');
  return [{
      external_urls: { spotify: '' },
      href: '',
      id: '0',
      name: decodeStr(fallbackName),
      type: 'artist',
      uri: `spotify:artist:0`,
      image: extractImage(item.image)
  }];
}

const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function getDownloadUrl(song: any): string {
  if (!song || !song.id) return '';
  return `${BASE_API_URL}/songs/${song.id}/stream?quality=320kbps`;
}

export function mapJioSaavnSongToSpotifyTrack(song: any): Track & { uid?: string } {
  const albumName = typeof song.album === 'object' ? (song.album?.name || song.album?.title) : (song.album || song.more_info?.album);
  const rawAlbumId = typeof song.album === 'object' ? song.album?.id : (song.album_id || song.more_info?.album_id);
  const albumId = (rawAlbumId && rawAlbumId !== '0') ? rawAlbumId : song.id;
  
  return {
    album: {
      album_type: 'single',
      artists: [],
      available_markets: [],
      external_urls: { spotify: '' },
      href: '',
      id: albumId || '',
      images: extractImage(song.image),
      name: decodeStr(albumName || song.title || song.name || 'Unknown Album'),
      release_date: song.year || (song.releaseDate ? song.releaseDate.split('-')[0] : '2024'),
      release_date_precision: 'year',
      total_tracks: 1,
      type: 'album',
      uri: `spotify:album:${albumId || '0'}`
    },
    artists: getArtists(song),
    available_markets: [],
    disc_number: 1,
    duration_ms: (parseInt(song.duration) || 0) * 1000,
    explicit: song.explicitContent === '1' || song.explicitContent === true,
    external_ids: { isrc: '' },
    external_urls: { spotify: song.url || '' },
    href: '',
    id: song.id,
    uid: song.uid || (song.id + '_' + Math.random().toString(36).substr(2, 9)),
    is_local: false,
    is_playable: true,
    name: decodeStr(song.title || song.name || 'Unknown Track'),
    popularity: 50,
    preview_url: getDownloadUrl(song),
    track_number: 1,
    type: 'track',
    uri: `spotify:track:${song.id}`,
    isLyricsMatch: !!song.isLyricsMatch,
    matchedLyricsSnippet: song.matchedLyricsSnippet || undefined
  };
}

export function mapJioSaavnAlbumToSpotifyAlbum(album: any): Album {
  return {
    album_type: album.type || (album.songCount === 1 ? 'single' : 'album'),
    artists: getArtists(album),
    available_markets: [],
    external_urls: { spotify: album.url || '' },
    href: '',
    id: album.id,
    images: extractImage(album.image),
    name: decodeStr(album.title || album.name || 'Unknown Album'),
    release_date: album.year ? String(album.year) : '2024',
    release_date_precision: 'year',
    total_tracks: album.songCount || (album.songs?.length ?? 0),
    type: 'album',
    uri: `spotify:album:${album.id}`
  };
}

export function mapJioSaavnPlaylistToSpotifyPlaylist(playlist: any): Playlist {
  return {
    collaborative: false,
    description: decodeStr(playlist.subtitle || ''),
    external_urls: { spotify: playlist.url || '' },
    href: '',
    id: playlist.id,
    images: extractImage(playlist.image),
    followers: { href: '', total: 0 },
    name: decodeStr(playlist.name || playlist.title || 'Unknown Playlist'),
    owner: { id: '0', display_name: 'JioSaavn User', href: '', type: 'user', uri: '', external_urls: { spotify: '' }, followers: { href: null, total: 0 } },
    public: true,
    snapshot_id: '',
    tracks: { href: '', total: playlist.songCount || 0 },
    type: 'playlist',
    uri: `spotify:playlist:${playlist.id}`
  };
}

export function mapJioSaavnArtistToSpotifyArtist(artist: any): Artist {
  return {
    external_urls: { spotify: artist.url || '' },
    followers: { href: '', total: artist.followerCount || 0 },
    genres: [],
    href: '',
    id: artist.id,
    images: extractImage(artist.image),
    name: decodeStr(artist.title || artist.name || 'Unknown Artist'),
    popularity: 50,
    type: 'artist',
    uri: `spotify:artist:${artist.id}`
  };
}

export function mapJioSaavnShowToSpotifyEpisode(show: any): any {
  return {
    audio_preview_url: show.url || '',
    description: decodeStr(show.subtitle || show.description || ''),
    duration_ms: 1800000,
    explicit: show.explicit_content === '1' || show.explicitContent === true,
    external_urls: { spotify: show.perma_url || show.url || '' },
    href: '',
    id: show.id,
    images: extractImage(show.image),
    is_externally_hosted: false,
    is_playable: true,
    languages: [],
    name: decodeStr(show.title || show.name || 'Unknown Podcast'),
    release_date: show.more_info?.release_date || show.year || '2023-01-01',
    release_date_precision: 'day',
    type: 'episode',
    uri: `spotify:episode:${show.id}`,
    show: {
      available_markets: [],
      copyrights: [],
      description: decodeStr(show.subtitle || ''),
      episodes: { href: '', total: 0 },
      external_urls: { spotify: show.perma_url || show.url || '' },
      href: '',
      id: show.id,
      images: extractImage(show.image),
      is_externally_hosted: false,
      languages: [],
      media_type: 'audio',
      name: decodeStr(show.title || show.name || 'Unknown Show'),
      publisher: 'JioSaavn',
      total_episodes: 0,
      type: 'show',
      uri: `spotify:show:${show.id}`
    }
  };
}

export function mapJioSaavnEpisodeToSpotifyEpisode(episode: any, show?: any): any {
  const showName = show?.name || show?.title || 'Podcast';
  const showImage = extractImage(show?.image);
  const epImage = extractImage(episode.image);
  const images = epImage.length > 0 ? epImage : showImage;
  const rawDuration = parseInt(episode.duration, 10);
  const duration_ms = !isNaN(rawDuration) && rawDuration > 0 ? rawDuration * 1000 : 1800000;

  return {
    audio_preview_url: `${BASE_API_URL}/songs/${episode.id}/stream?quality=320kbps`,
    description: decodeStr(episode.subtitle || episode.description || show?.description || ''),
    duration_ms,
    explicit: episode.explicit_content === '1' || episode.explicitContent === true,
    external_urls: { spotify: episode.perma_url || episode.url || '' },
    href: '',
    id: episode.id,
    images,
    is_externally_hosted: false,
    is_playable: true,
    languages: show?.language ? [show.language] : [],
    name: decodeStr(episode.name || episode.title || 'Episode'),
    release_date: episode.releaseDate || episode.release_date || show?.release_date || '2023-01-01',
    release_date_precision: 'day',
    type: 'episode',
    uri: `spotify:episode:${episode.id}`,
    show: {
      available_markets: [],
      copyrights: [],
      description: decodeStr(show?.description || show?.subtitle || ''),
      episodes: { href: '', total: show?.totalEpisodes || 0 },
      external_urls: { spotify: show?.perma_url || show?.url || '' },
      href: '',
      id: show?.id || episode.id,
      images: showImage.length > 0 ? showImage : images,
      is_externally_hosted: false,
      languages: show?.language ? [show.language] : [],
      media_type: 'audio',
      name: decodeStr(showName),
      publisher: decodeStr(showName),
      total_episodes: show?.totalEpisodes || 0,
      type: 'show',
      uri: `spotify:show:${show?.id || episode.id}`
    }
  };
}

